/**
 * Nets of 3D Shapes generator (6.MGSR.1.2).
 *
 * Reason about nets: how many faces a solid's net has, which solid a described
 * net folds into, and which net description matches a given solid. All families
 * are multiple choice; the correct option is derived from a fixed catalog of
 * solids so the prompt and the graded choice always agree. No graph is drawn -
 * the harness only validates a graph's kind when one is present.
 *
 * Families:
 *   F1: face count of a solid's net (choice)
 *   F2: description of a net -> which solid (choice)
 *   F3: which solid -> net description (choice)
 */

import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "nets-3d" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

interface Solid {
  readonly name: string;
  readonly faces: number;
  /** Human description of the net's faces. */
  readonly net: string;
}

const SOLIDS: readonly Solid[] = [
  { name: "cube", faces: 6, net: "6 congruent squares" },
  { name: "rectangular prism", faces: 6, net: "6 rectangles in 3 congruent pairs" },
  { name: "triangular prism", faces: 5, net: "2 triangles and 3 rectangles" },
  { name: "square pyramid", faces: 5, net: "1 square and 4 triangles" },
  { name: "triangular pyramid", faces: 4, net: "4 triangles" },
];

interface Cfg {
  readonly families: readonly Family[];
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"] },
  standard: { families: ["F1", "F2", "F3"] },
  hard: { families: ["F1", "F2", "F3"] },
  challenge: { families: ["F2", "F3"] },
};

export class Nets3DGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const solid = rng.pick(SOLIDS);

      if (family === "F1") {
        // How many faces does this solid's net have? (choice among counts)
        const correct = solid.faces;
        // Distractors are distinct face counts, never equal to the correct one.
        const distractorPool = [4, 5, 6, 7, 8].filter((n) => n !== correct);
        const distractors = rng.shuffle(distractorPool).slice(0, 3);
        const shuffled = rng.shuffle([correct, ...distractors]);
        const choices: Choice[] = shuffled.map((n, i) => ({ id: `c${i}`, label: `${n}` }));
        const correctIdx = shuffled.indexOf(correct);
        const correctId = `c${correctIdx}`;
        return {
          id: `${TOPIC}-${ctx.seed}-${family}`,
          generator: TOPIC,
          generatorVersion: VERSION,
          seed: ctx.seed,
          unit: topic.unit,
          topic: TOPIC,
          family,
          difficulty: ctx.difficulty,
          prompt: { text: `How many faces does the net of a ${solid.name} have?` },
          answerType: "choice",
          answer: { kind: "choice", value: correctId },
          steps: [
            {
              description: `A net has one flat piece for each face of the solid. A ${solid.name} has ${solid.faces} faces.`,
              latex: `${solid.faces}`,
            },
          ],
          hints: [
            { level: 1, text: "Count each flat surface (face) of the solid." },
            { level: 2, text: "The net has exactly one region per face." },
          ],
          subskills: topic.subskills,
          choices,
        };
      }

      if (family === "F2") {
        // Net description -> which solid.
        const options = rng.shuffle(SOLIDS).slice(0, 4);
        if (!options.some((s) => s.name === solid.name)) options[0] = solid;
        const shuffled = rng.shuffle(options);
        const choices: Choice[] = shuffled.map((s, i) => ({ id: `c${i}`, label: s.name }));
        const correctIdx = shuffled.findIndex((s) => s.name === solid.name);
        const correctId = `c${correctIdx}`;
        return {
          id: `${TOPIC}-${ctx.seed}-${family}`,
          generator: TOPIC,
          generatorVersion: VERSION,
          seed: ctx.seed,
          unit: topic.unit,
          topic: TOPIC,
          family,
          difficulty: ctx.difficulty,
          prompt: { text: `A net is made of ${solid.net}. Which solid does it fold into?` },
          answerType: "choice",
          answer: { kind: "choice", value: correctId },
          steps: [
            {
              description: `${solid.net} fold into a ${solid.name}.`,
              latex: `\\text{${solid.name}}`,
            },
          ],
          hints: [
            { level: 1, text: "Match the shapes in the net to the faces of each solid." },
            { level: 2, text: "Triangles on the sides usually mean a prism or pyramid, not a box." },
          ],
          subskills: topic.subskills,
          choices,
        };
      }

      // F3: which net description matches this solid?
      const options = rng.shuffle(SOLIDS).slice(0, 4);
      if (!options.some((s) => s.name === solid.name)) options[0] = solid;
      const shuffled = rng.shuffle(options);
      const choices: Choice[] = shuffled.map((s, i) => ({ id: `c${i}`, label: s.net }));
      const correctIdx = shuffled.findIndex((s) => s.name === solid.name);
      const correctId = `c${correctIdx}`;
      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: `Which net folds into a ${solid.name}?` },
        answerType: "choice",
        answer: { kind: "choice", value: correctId },
        steps: [
          {
            description: `A ${solid.name} unfolds into ${solid.net}.`,
            latex: `\\text{${solid.net}}`,
          },
        ],
        hints: [
          { level: 1, text: "Think about how many faces the solid has and their shapes." },
          { level: 2, text: `A ${solid.name} has ${solid.faces} faces.` },
        ],
        subskills: topic.subskills,
        choices,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "choice" ? q.answer.value : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const nets3d = new Nets3DGenerator();
